import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '~components/dialog';
import { IconButtonBase } from '~components/icon-button';
import { BasicClose } from '~svg-components';
import StepFindStudent from './step-find-student';
import StepNewBooking from './step-new-booking';
import StepNewStudent from './step-new-student';
import StepStudentFound from './step-student-found';

export const findStudent = Symbol('findStudent');
export const newBooking = Symbol('newBooking');
export const newStudent = Symbol('newStudent');
export const studentFound = Symbol('studentFound');

function CreateDirectLetBookingModalContent(props) {
  const { t } = useTranslation('booking');
  const {
    propertyId,
    defaultStep = findStudent,
    defaultContentProps = {},
    onBookingCreated,
    className,
    style,
  } = props;

  const [step, setStep] = useState(defaultStep);
  const [contentProps, setContentProps] = useState(defaultContentProps);

  const content = useMemo(() => {
    switch (step) {
      case findStudent:
        return (
          <StepFindStudent
            title={t('create_direct_let_booking')}
            propertyId={propertyId}
            onClickStudent={({ student, searchValue }) => {
              setContentProps({ student, searchValue });
              setStep(studentFound);
            }}
            onCreateNewStudent={({ searchValue }) => {
              setContentProps({ searchValue });
              setStep(newStudent);
            }}
            {...contentProps}
          />
        );
      case studentFound:
        return (
          <StepStudentFound
            onClickBackToSearch={() => {
              setContentProps({
                defaultSearchValue: contentProps.searchValue,
                defaultShowResultList: true,
              });
              setStep(findStudent);
            }}
            onClickCreateBooking={({ student }) => {
              setContentProps({ student });
              setStep(newBooking);
            }}
            {...contentProps}
          />
        );
      case newBooking:
        return <StepNewBooking onBookingCreated={onBookingCreated} {...contentProps} />;
      case newStudent:
        return (
          <StepNewStudent
            onStudentCreated={(student) => {
              setContentProps({ student: student.studentInfo });
              setStep(newBooking);
            }}
            {...contentProps}
          />
        );
      default:
        throw new Error('Unknown step');
    }
  }, [contentProps, onBookingCreated, propertyId, step, t]);

  return (
    <DialogContent className={className} style={style}>
      {content}
    </DialogContent>
  );
}

CreateDirectLetBookingModalContent.propTypes = {
  propertyId: PropTypes.string.isRequired,
  className: PropTypes.string,
  defaultContentProps: PropTypes.object,
  defaultStep: PropTypes.symbol,
  onBookingCreated: PropTypes.func,
  style: PropTypes.object,
};

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 'auto',
    minWidth: 560,
    minHeight: 320,
  },

  closeButton: {
    marginTop: '16px',
    marginRight: '16px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    '& svg': { fill: theme.colors.grey66 },
    '&:hover': { '& svg': { fill: theme.colors.brandAction } },
  },
}));

function CreateDirectLetBookingModal(props) {
  const { open, onClose, ...otherProps } = props;
  const classes = useStyles();

  return (
    <Dialog
      classes={{ paper: classes.paper, scrollPaper: classes.scrollPaper }}
      open={open}
      onClose={onClose}
    >
      <>
        <IconButtonBase className={classes.closeButton} onClick={onClose}>
          <BasicClose />
        </IconButtonBase>

        <CreateDirectLetBookingModalContent {...otherProps} />
      </>
    </Dialog>
  );
}

CreateDirectLetBookingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default CreateDirectLetBookingModal;
